import * as React from 'react';

import { AutocompleteProps } from '@lightspeed/flame/Autocomplete';
import debounce from 'lodash/debounce';
import lowerCase from 'lodash/lowerCase';

import { createReactRenderer } from 'helpers/react-renderer';
import SerialsAutocomplete2 from 'root/apps/Autocomplete/Serials/SerialsAutocomplete2';
import { useScanReactSelect } from 'root/scan/useScanReactSelect';
import Modules from 'shared_modules/Modules';

import Autocomplete from '..';
import GuiDefHiddenInput from '../GuiDefHiddenInput';
import {
  getMountProps,
  reinstateSerialOnTransactionLine,
  removeLineItemSerializedInfoFromStorage,
  searchSerials,
  SerialsAutocompleteOption,
} from './utils';

let isScanWithEnter = false;
const ENFORCE_SHIPPING_SERIAL_NUMBER_MODULE = 'shipping.enforce_serial_numbers';
const COMPLETE_SALE_WITHOUT_SERIAL_NUMBER_RIGHT: string =
  'register_complete_a_sale_without_selecting_a_serial_number';

const SerialsAutocomplete: React.FC<AutocompleteProps> = ({
  isAsync = true,
  defaultValue,
  placeholder = 'Scan or type a serial number',
  hiddenInput,
  defaultOptions,
  hasSubmitFormButton,
  onChange,
  inputId: _inputId,
  ...rest
}) => {
  const [value, setValue] = React.useState(defaultValue);
  const [isDisabled, setIsDisabled] = React.useState(rest.isDisabled || false);
  const submitRef = React.useRef<HTMLButtonElement>(null);
  const enforceSerialNumbers = window.lsCloud.Modules.hasModule(
    ENFORCE_SHIPPING_SERIAL_NUMBER_MODULE
  );
  const canCompleteSaleWithoutSerialNumber = window.merchantos.session.hasRight(
    COMPLETE_SALE_WITHOUT_SERIAL_NUMBER_RIGHT
  );
  const canReinstateSerialNumbers =
    enforceSerialNumbers === true && canCompleteSaleWithoutSerialNumber === false;

  const serializedItemID = rest.itemId;

  const isSerializedItemOnWorkorder = !!rest.workorderItemID;

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Hack: only allow enter key to submit the form if there is at least one option.
    // If a scan is performed and the scan suffix is "Enter", keep it in memory for when the loadOptions
    // debounced function completes. If there is one result, it's a perfect match and auto submit.
    if (e.key === 'Enter' && !document.querySelectorAll('.cr-autocomplete__option').length) {
      isScanWithEnter = true;
      e.preventDefault();
    } else {
      isScanWithEnter = false;
    }
  };
  const inputId = useScanReactSelect({ onKeyDown, inputId: _inputId });

  const loadSuggestions = (query: string, callback: (args: any) => any) => {
    searchSerials({ query, itemId: serializedItemID.toString() }).then((res) => {
      if (
        isScanWithEnter &&
        query.length > 7 &&
        res.length === 1 &&
        lowerCase(res[0].serial) === lowerCase(query)
      ) {
        setValue(res[0]);
      }
      return callback(res);
    });
  };

  // Make sure the fn is cached so the debounce is correctly working else a new function will
  // be created when the component updates and the previous function won't be debounced
  const debouncedLoadSuggestions = React.useCallback(debounce(loadSuggestions, 300), []);

  const { value: hiddenInputValue = '' } = value || {};

  React.useEffect(() => {
    // A part of avoiding requiring two reloads to reinstate the serial number to the line item if the form's data has changed.
    // @ts-ignore
    if (window.oldConfirm !== undefined) {
      // @ts-ignore
      window.confirm = window.oldConfirm;
    }
  }, []);

  React.useEffect(() => {
    if (value) {
      setIsDisabled(true);

      if (hasSubmitFormButton) {
        submitRef.current?.click();
      } else if (onChange === 'listingAssignItemSerial') {
        window.listingAssignItemSerial(
          rest.panelId,
          rest.transactionLineId,
          rest.customerId,
          value.value,
          false
        );

        if (canReinstateSerialNumbers) {
          removeLineItemSerializedInfoFromStorage(rest.transactionLineId);
        }
      } else if (isSerializedItemOnWorkorder) {
        window.merchantos.edit_workorder.addSerialized(rest.workorderItemID, value.value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  React.useEffect(() => {
    function reinstateHandler() {
      if (canReinstateSerialNumbers) {
        reinstateSerialOnTransactionLine(
          rest.transactionLineId as number,
          rest.panelId as string,
          rest.customerId as string
        );
      }
    }

    // page reload/refresh
    window.addEventListener('beforeunload', reinstateHandler);

    // page close
    window.addEventListener('unload', reinstateHandler);

    // anything else (redirect, any kind of component dismount, etc.)
    return () => {
      reinstateHandler();

      window.removeEventListener('beforeunload', reinstateHandler);
      window.removeEventListener('unload', reinstateHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Autocomplete
        {...rest}
        inputId={inputId}
        value={value}
        defaultOptions={defaultOptions}
        loadOptions={debouncedLoadSuggestions}
        placeholder={placeholder}
        isAsync={isAsync}
        onChange={setValue}
        noResultsText={null}
        formatOptionLabel={(option: SerialsAutocompleteOption) => {
          const { serial, colorName, sizeName } = option;

          option.label = `${serial}${colorName ? ` Color: ${colorName}` : ''}${
            sizeName ? `, Size: ${sizeName}` : ''
          }`;

          return option;
        }}
        components={{
          DropdownIndicator: () => null,
        }}
        isDisabled={isDisabled}
        id="serials-autocomplete"
      />

      {hasSubmitFormButton ? (
        <button type="submit" ref={submitRef} style={{ display: 'none' }} />
      ) : null}

      {hiddenInput ? (
        <GuiDefHiddenInput {...hiddenInput} name="serialized_id" defaultValue={hiddenInputValue} />
      ) : null}
    </>
  );
};

export default SerialsAutocomplete;

const RenderedComponent = createReactRenderer({
  component: SerialsAutocomplete,
  selector: '[data-react-root="SerialsAutocomplete"]',
});
const RenderedComponent2 = createReactRenderer({
  component: SerialsAutocomplete2,
  selector: '[data-react-root="SerialsAutocomplete"]',
});

history.onChangeState(
  [
    { pathname: '/register.php', form_name: undefined, view: undefined },
    {
      form_name: 'view',
      name: ['ship_to.views.ship_to'],
    },
    { name: 'workbench.views.beta_workorder', tab: 'details' },
  ],
  (filters: HistoryFilter) => {
    if (Modules.hasModule('inventory_count.scan_refactor')) {
      RenderedComponent2.mount({ props: getMountProps(filters) });
    } else {
      RenderedComponent.mount({ props: getMountProps(filters) });
    }
  },
  () => {
    if (Modules.hasModule('inventory_count.scan_refactor')) {
      RenderedComponent2.unmount();
    } else {
      RenderedComponent.unmount();
    }
  }
);
